// © 2026 AttainIP Engineering
/**
 * SelectOrganisation — the Select-Organisation step of admin sign-in (RFC-0510 W28).
 *
 * Rendered by `CallbackPage` when Identity answered with an org-selection
 * challenge instead of a token pair, which it does for any operator holding more
 * than one ACTIVE membership (RFC-0510 W05, wired onto the login paths by W15).
 * Before this step existed, the console called `setTokens(tokens.access_token,
 * …)` on that shape, wrote the string `"undefined"` as the admin token, and the
 * sign-in died at `/v1/auth/me`.
 *
 * ⚠️ THE ORG-SELECTION TOKEN NEVER LEAVES THIS COMPONENT'S REACT STATE.
 * It grants nothing and is short-lived, so it is held in a prop — never in
 * `localStorage` via `setTokens`, never in a URL — and the only place it is ever
 * sent is the exchange endpoint, in the request BODY.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  exchangeOrgSelection,
  type OrganizationChoice,
  type OrgSelectionRequiredResponse,
  type TokenResponse,
} from '@/api/identity';
import { orgChoiceLabel, orgChoiceSubLabel } from '@/lib/orgChoiceLabel';

type Phase = 'choosing' | 'exchanging' | 'expired';

export interface SelectOrganisationProps {
  selection: OrgSelectionRequiredResponse;
  /** Called with the REAL token pair once an exchange succeeds. This component
   *  never establishes the session itself. */
  onExchanged: (tokens: TokenResponse) => void | Promise<void>;
}

export function SelectOrganisation({ selection, onExchanged }: SelectOrganisationProps) {
  const [phase, setPhase] = useState<Phase>('choosing');
  const [notice, setNotice] = useState<string | null>(null);
  const [pendingOrgId, setPendingOrgId] = useState<string | null>(null);

  // `expires_in` is seconds from when Identity minted the token, which is near
  // enough to now — the response has only just arrived.
  const deadline = useMemo(
    () => Date.now() + selection.expires_in * 1000,
    [selection.expires_in],
  );

  useEffect(() => {
    const remaining = deadline - Date.now();
    if (remaining <= 0) {
      setPhase('expired');
      return;
    }
    const timer = setTimeout(() => setPhase('expired'), remaining);
    return () => clearTimeout(timer);
  }, [deadline]);

  const choose = useCallback(
    async (choice: OrganizationChoice) => {
      if (phase !== 'choosing') return;
      if (Date.now() >= deadline) {
        setPhase('expired');
        return;
      }

      setPhase('exchanging');
      setPendingOrgId(choice.organization_id);
      setNotice(null);

      const result = await exchangeOrgSelection(
        selection.org_selection_token,
        choice.organization_id,
      );

      if (result.ok) {
        try {
          await onExchanged(result.tokens);
        } catch (err) {
          setPhase('choosing');
          setPendingOrgId(null);
          setNotice(
            err instanceof Error
              ? err.message
              : 'We could not finish signing you in. Please try again.',
          );
        }
        return;
      }

      if (result.reason === 'selection_expired') {
        setPhase('expired');
        setPendingOrgId(null);
        return;
      }

      setPhase('choosing');
      setPendingOrgId(null);
      setNotice(
        result.reason === 'membership_revoked'
          ? `You no longer have access to ${orgChoiceLabel(choice)}. Your ` +
            'membership may have been removed. Choose another organisation.'
          : result.message,
      );
    },
    [phase, deadline, selection.org_selection_token, onExchanged],
  );

  if (phase === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md" data-testid="org-selection-expired">
          <p className="text-gray-900 font-medium mb-2">Your sign-in window expired</p>
          <p className="text-sm text-gray-600 mb-4">
            You took a little too long to choose an organisation. Please sign in again.
          </p>
          <a
            href={`${import.meta.env.BASE_URL}login`}
            className="text-sm text-blue-600 hover:underline"
          >
            Back to login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="w-full max-w-md" data-testid="org-selection-picker">
        <h1 className="text-lg font-medium text-gray-900 mb-1 text-center">
          Choose an organisation
        </h1>
        <p className="text-sm text-gray-600 mb-6 text-center">
          Your account has access to more than one organisation. Choose the one
          you want to work in.
        </p>

        {notice && (
          <p role="alert" className="text-sm text-red-600 border border-red-200 bg-red-50 p-3 mb-4">
            {notice}
          </p>
        )}

        <ul className="flex flex-col gap-2">
          {selection.organizations.map((choice) => {
            const label = orgChoiceLabel(choice);
            const subLabel = orgChoiceSubLabel(choice);
            return (
              <li key={choice.organization_id}>
                <button
                  type="button"
                  onClick={() => choose(choice)}
                  disabled={phase === 'exchanging'}
                  className="w-full text-left border border-gray-200 bg-white px-4 py-3 hover:border-gray-400 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <span className="block text-gray-900">{label}</span>
                  {subLabel && <span className="block text-xs text-gray-500">{subLabel}</span>}
                  {pendingOrgId === choice.organization_id && (
                    <span className="block text-xs text-gray-500">Signing you in…</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
